'use client';

import {generateSubmissionFlowId} from 'common/submission_flow_id';
import {useAuth} from 'iq-auth';
import {AuthForm} from 'iq-auth/components/auth_form';
import {FlowTakehomeUpload} from 'iq-interview_experiences/components/flow_takehome_upload';
import type {TakeHomeFile} from 'iq-interview_experiences/types';
import type {SubmittedTakehomeFile} from 'iq-takehomes/api';
import {getTakehomeUploadUrl, submitTakeHome, validateTakehomeFiles} from 'iq-takehomes/api';
import {trackTakeHomeShareSubmitted} from 'iq-takehomes/tracking';
import {CheckCircle} from 'lucide-react';
import Link from 'next/link';
import {useState} from 'react';

const PENDING_SHARE_KEY = 'iq:pending-takehome-share';

function shareThanks(isUnlimited: boolean) {
  if (isUnlimited) return 'Thanks for helping future candidates prepare.';
  return "You've unlocked 1 new take-home. Thanks for helping future candidates prepare.";
}

function ShareSuccessScreen({
  onContinue,
  continueLabel,
  isUnlimited,
}: {
  onContinue?: () => void;
  continueLabel: string;
  isUnlimited: boolean;
}) {
  return (
    <div className='flex flex-col items-center gap-5 text-center py-4'>
      <div className='w-12 h-12 rounded-full bg-green-100 flex items-center justify-center'>
        <CheckCircle className='w-6 h-6 text-green-600' strokeWidth={1.75} />
      </div>
      <div className='flex flex-col gap-1'>
        <h2 className='text-xl font-bold text-iq-black'>Take-home shared!</h2>
        <p className='text-sm text-iq-grey-medium leading-relaxed'>
          {shareThanks(isUnlimited)}
        </p>
      </div>
      {onContinue ? (
        <button
          type='button'
          onClick={onContinue}
          className='px-6 py-2.5 bg-iq-black text-white text-sm font-semibold rounded-lg hover:opacity-90 transition-opacity'>
          {continueLabel}
        </button>
      ) : (
        <Link
          href='/takehomes'
          className='px-6 py-2.5 bg-iq-black text-white text-sm font-semibold rounded-lg hover:opacity-90 transition-opacity'>
          Browse take-homes
        </Link>
      )}
    </div>
  );
}

type Props = {
  compact?: boolean;
  onSuccess?: () => void;
  /** Fires once the share completes (success screen shown), before any dismissal. */
  onShared?: () => void;
  /** Label for the success CTA. Defaults to the list-context copy. */
  continueLabel?: string;
};

export function ShareFlow({compact, onSuccess, onShared, continueLabel = 'Browse take-homes'}: Props) {
  const {user, isPremium} = useAuth();
  const isUnlimited = isPremium || user.isAdmin;
  const [submissionFlowId] = useState(() => generateSubmissionFlowId());
  const [files, setFiles] = useState<Array<TakeHomeFile>>([]);
  const [validating, setValidating] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showAuth, setShowAuth] = useState(false);
  const [done, setDone] = useState(false);

  async function handleAddFiles(incoming: File[]) {
    const newEntries: Array<TakeHomeFile> = incoming.map((f) => ({
      id: `${f.name}-${f.size}-${Date.now()}`,
      name: f.name,
      size: f.size,
      s3Key: '',
      category: 'unknown' as const,
      uploading: true,
      error: null,
    }));
    setFiles((prev) => [...prev, ...newEntries]);

    await Promise.all(
      newEntries.map(async (entry, i) => {
        const file = incoming[i];
        if (!file) return;
        try {
          const {uploadUrl, s3Key, fields} = await getTakehomeUploadUrl(
            entry.name,
            file.type || 'application/octet-stream',
            submissionFlowId,
          );
          const form = new FormData();
          for (const [key, value] of Object.entries(fields)) form.append(key, value);
          form.append('file', file);
          const res = await fetch(uploadUrl, {method: 'POST', body: form});
          if (!res.ok) {
            const msg = res.status === 403 ? 'File is too large. Maximum size is 25 MB.' : 'Upload failed. Please try again.';
            setFiles((prev) =>
              prev.map((f) => (f.id === entry.id ? {...f, uploading: false, error: msg} : f)),
            );
            return;
          }
          setFiles((prev) =>
            prev.map((f) => (f.id === entry.id ? {...f, uploading: false, s3Key} : f)),
          );
        } catch {
          setFiles((prev) =>
            prev.map((f) =>
              f.id === entry.id
                ? {...f, uploading: false, error: 'Upload failed. Please try again.'}
                : f,
            ),
          );
        }
      }),
    );
  }

  function handleRemoveFile(id: string) {
    setFiles((prev) => prev.filter((f) => f.id !== id));
  }

  async function doSubmit(flowId: string, sharedFiles: Array<SubmittedTakehomeFile>) {
    setSubmitting(true);
    try {
      await submitTakeHome(flowId, sharedFiles);
      void trackTakeHomeShareSubmitted();
      localStorage.removeItem(PENDING_SHARE_KEY);
      setDone(true);
      onShared?.();
    } catch {
      setError('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }

  async function handleSubmit() {
    setError(null);

    const uploaded = files.filter((f) => f.s3Key !== '' && !f.error);
    if (uploaded.length === 0) return;

    setValidating(true);
    let sharedFiles: Array<SubmittedTakehomeFile> = [];
    try {
      const result = await validateTakehomeFiles(
        uploaded.map((f) => ({filename: f.name, size: f.size, contentType: '', s3Key: f.s3Key})),
      );
      if (!result.valid) {
        setError(
          result.feedback || 'Files do not appear to be a take-home assignment. Please try again.',
        );
        return;
      }
      const categoryMap = new Map(result.fileCategories.map((c) => [c.filename, c.category]));
      sharedFiles = uploaded.map((f) => ({
        filename: f.name,
        s3Key: f.s3Key,
        category: categoryMap.get(f.name) ?? 'unknown',
      }));
    } catch {
      setError('Failed to validate files. Please try again.');
      return;
    } finally {
      setValidating(false);
    }

    localStorage.setItem(PENDING_SHARE_KEY, JSON.stringify({submissionFlowId, files: sharedFiles}));

    if (!user.loggedIn) {
      setShowAuth(true);
      return;
    }

    await doSubmit(submissionFlowId, sharedFiles);
  }

  async function handleAuthSuccess() {
    const raw = localStorage.getItem(PENDING_SHARE_KEY);
    if (!raw) return;
    try {
      const data = JSON.parse(raw) as {submissionFlowId: string; files: Array<SubmittedTakehomeFile>};
      await doSubmit(data.submissionFlowId, data.files ?? []);
    } catch {
      localStorage.removeItem(PENDING_SHARE_KEY);
      setError('Something went wrong. Please try again.');
      setShowAuth(false);
    }
  }

  const isAnyUploading = files.some((f) => f.uploading);
  const hasUploaded = files.some((f) => f.s3Key !== '' && !f.error);
  const canSubmit = hasUploaded && !isAnyUploading && !validating && !submitting;

  if (done) {
    const successContent = (
      <ShareSuccessScreen onContinue={onSuccess} continueLabel={continueLabel} isUnlimited={isUnlimited} />
    );
    if (compact) return <div className='p-8'>{successContent}</div>;
    return (
      <div className='min-h-[calc(100vh-4rem)] flex flex-col items-center justify-center px-6 pt-8 pb-24 bg-iq-grey-lighter'>
        <div className='w-full max-w-3xl'>{successContent}</div>
      </div>
    );
  }

  if (showAuth) {
    const authContent = (
      <div className='p-8'>
        <AuthForm onBack={() => setShowAuth(false)} onSuccess={handleAuthSuccess} />
      </div>
    );
    if (compact) return authContent;
    return (
      <div className='min-h-[calc(100vh-4rem)] flex flex-col items-center justify-center px-6 pt-8 pb-24 bg-iq-grey-lighter'>
        <div className='w-full max-w-3xl'>{authContent}</div>
      </div>
    );
  }

  const content = (
    <div className='flex flex-col gap-6'>
      <div>
        <h1 className='text-2xl font-bold text-iq-black leading-snug'>
          Upload your take-home assignment.
        </h1>
        <p className='text-iq-grey-medium mt-2 text-base leading-relaxed'>
          Upload the files you received — instructions, prompt, or a ZIP of the full project.
          Future candidates will know exactly what to expect before they apply.
        </p>
      </div>

      <FlowTakehomeUpload
        files={files}
        onAddFiles={handleAddFiles}
        onRemoveFile={handleRemoveFile}
        disabled={validating || submitting}
      />

      {error && <p className='text-sm text-iq-red'>{error}</p>}

      <div className='flex justify-end'>
        <button
          type='button'
          disabled={!canSubmit}
          onClick={handleSubmit}
          className='flex items-center gap-2 px-6 py-3 bg-iq-black text-white text-sm font-semibold rounded-lg hover:opacity-90 transition-opacity disabled:opacity-40 disabled:cursor-not-allowed'>
          {validating ? 'Validating…' : submitting ? 'Sharing…' : 'Share take-home'}
        </button>
      </div>
    </div>
  );

  if (compact) {
    return <div className='p-8'>{content}</div>;
  }

  return (
    <div className='min-h-[calc(100vh-4rem)] flex flex-col items-center justify-center px-6 pt-8 pb-24 bg-iq-grey-lighter'>
      <div className='w-full max-w-3xl'>{content}</div>
    </div>
  );
}
